import React from 'react';

import { Document, Text, View } from '@react-pdf/renderer';

import { fmtDate, fmtDateTimeLong, fmtQty, isExpired, sum } from '../format';
import { GOODS_ISSUE_STATUS } from '../status';
import type { GoodsIssueDetail, GoodsIssueLine } from '../types';
import {
  PdfA4,
  type PdfColumn,
  PdfFooter,
  PdfHeader,
  PdfKV,
  PdfKVRow,
  PdfSignatures,
  PdfTable,
  pdfStyles,
  type PrintMeta,
  T,
} from './pdfKit';

/**
 * Picking list (any GI status): lines in bin-walk order with the storage name path, SKU,
 * batch number, expiry, qty to take, a write-in "qty diambil" box and a tick box;
 * picker + checker signatures; QR of the GI code.
 */

export const sortByBin = (lines: GoodsIssueLine[]): GoodsIssueLine[] =>
  [...lines].sort(
    (a, b) =>
      (a.storagePath || a.storageName).localeCompare(
        b.storagePath || b.storageName,
        undefined,
        { numeric: true }
      ) || a.sku.localeCompare(b.sku)
  );

const PickingListPdf: React.FC<{
  detail: GoodsIssueDetail;
  meta: PrintMeta;
}> = ({ detail, meta }) => {
  const gi = detail.goodsIssue;
  const oo = detail.outboundOrder;
  const lines = sortByBin(detail.lines);
  const status = GOODS_ISSUE_STATUS[gi.status]?.label;

  const columns: PdfColumn<GoodsIssueLine>[] = [
    { title: 'No', width: 20, fixed: true, render: (_l, i) => <T>{i + 1}</T> },
    {
      title: 'Storage',
      width: 2,
      render: (l) => <T bold>{l.storagePath || l.storageName}</T>,
    },
    {
      title: 'SKU / barang',
      width: 2.2,
      render: (l) => (
        <View>
          <T mono bold>
            {l.sku}
          </T>
          <T>{l.itemName}</T>
        </View>
      ),
    },
    {
      // `YYYYMMDD-MMDDYY-XXXX` = 20 Courier chars at 9pt ≈ 108pt: fixed so
      // it never runs into Exp.
      title: 'Batch number',
      width: 116,
      fixed: true,
      render: (l) => <T mono>{l.batchNumber}</T>,
    },
    {
      title: 'Exp',
      width: 1.1,
      render: (l) => (
        <View>
          <T>{l.expiredDate ? fmtDate(l.expiredDate) : '—'}</T>
          {isExpired(l.expiredDate) && (
            <T bold small>
              EXPIRED
            </T>
          )}
        </View>
      ),
    },
    {
      title: 'Qty ambil',
      width: 1.1,
      align: 'right',
      render: (l) => (
        <Text>
          <Text style={{ fontFamily: 'Helvetica-Bold' }}>
            {fmtQty(l.qtyReserved)}
          </Text>{' '}
          {l.uomName}
        </Text>
      ),
    },
    {
      title: 'Qty diambil',
      width: 54,
      fixed: true,
      align: 'right',
      render: (l) =>
        l.qtyPicked ? (
          <T>{fmtQty(l.qtyPicked)}</T>
        ) : (
          <View style={pdfStyles.writeBox} />
        ),
    },
    {
      title: 'Cek',
      width: 24,
      fixed: true,
      align: 'center',
      render: () => <View style={pdfStyles.box} />,
    },
  ];

  return (
    <Document title={`Picking list ${gi.code}`}>
      <PdfA4>
        <PdfHeader
          title="Picking list"
          code={gi.code}
          meta={`Dibuat ${fmtDateTimeLong(gi.createdAt)} · ${gi.hub?.name ?? `Hub #${gi.hubID}`}`}
          qr={meta.qr}
        />
        <PdfKVRow>
          <PdfKV
            label="Outbound request"
            value={oo?.code ?? gi.reference}
            mono
          />
          <PdfKV label="Ref client" value={oo?.externalID} mono />
          <PdfKV
            label="Client"
            value={gi.client?.name ?? `Client #${gi.clientID}`}
          />
          <PdfKV
            label="Expected delivery"
            value={oo ? fmtDate(oo.expectedDeliveryDate) : null}
          />
        </PdfKVRow>
        <PdfKVRow>
          <PdfKV label="Picker" value={gi.pickerName ?? '…………………………'} />
          <PdfKV label="Status" value={status ?? gi.status} />
          <PdfKV label="Total baris" value={String(lines.length)} />
          <PdfKV
            label="Total qty"
            value={fmtQty(sum(lines.map((l) => l.qtyReserved)))}
          />
        </PdfKVRow>
        <PdfTable columns={columns} rows={lines} />
        <PdfSignatures labels={['Picker', 'Checker / admin gudang']} />
        <PdfFooter
          doc={`Picking list ${gi.code}`}
          printedAt={meta.printedAt}
          printedBy={meta.printedBy}
        />
      </PdfA4>
    </Document>
  );
};

export default PickingListPdf;
